import {
  Container,
  Description,
  TabContentIntro,
  TabPaneTab,
  TooltipIcon,
} from 'components/search/SearchResults/components';
import { Fragment, useEffect, useMemo } from 'react';
import { Spin, Tabs, Tooltip } from 'antd';
import { Sticky, StickyContainer } from 'react-sticky';

import ArticulationCreditList from 'components/credits/credit-lists/ArticulationCreditList';
import { EllipsisOutlined } from '@ant-design/icons';
import FlexContainer from 'components/common/FlexContainer';
import { Helmet } from 'react-helmet';
import PrecedentCreditList from 'components/credits/credit-lists/PrecedentCreditList';
import ResultNotFound from 'components/search/ResultNotFound';
import { SourceInstitution } from 'types/source-institution';
import TooltipText from 'components/common/TooltipText';
import { getFilteredArticulations } from 'utils/articulation';
import { getFilteredPrecedents } from 'utils/precedent';
import useArticulations from 'hooks/use-articulations';
import { useCourseSearchStore } from 'store/course-search';
import useFormSchema from 'hooks/use-form-schema';
import useIsMobile from 'hooks/use-is-mobile';
import { usePersistentStore } from 'store/persistent';
import usePrecedents from 'hooks/use-precedents';
import { useQueryParams } from 'hooks/use-query-params';
import useTranslation from 'hooks/use-translation';

type Props = {
  sourceInstitution: SourceInstitution;
};

type TabKeyProperties = {
  ARTICULATION: 'ARTICULATION';
  PRECEDENT: 'PRECEDENT';
};

const TabKey: TabKeyProperties = {
  ARTICULATION: 'ARTICULATION',
  PRECEDENT: 'PRECEDENT',
};

export default function SearchResults(props: Props): JSX.Element {
  const { searchTerm } = useCourseSearchStore();
  const { searchCategory, setSearchCategory } = usePersistentStore();
  const query = useQueryParams();
  const { sourceInstitution } = props;
  const translation = useTranslation();
  const articulations = useArticulations();
  const precedents = usePrecedents();
  const formSchema = useFormSchema();
  // const [activeTab, setActiveTab] = useState<keyof TabKeyProperties>(TabKey.ARTICULATION);
  const isMobile = useIsMobile();

  useEffect(() => {
    const tab = query.get('tab');
    if (!tab) {
      return;
    }
    if (Object.values(TabKey).includes(tab as keyof TabKeyProperties)) {
      setSearchCategory(tab as keyof TabKeyProperties);
    }
  }, [query, searchCategory, setSearchCategory]);

  const filteredArticulations = useMemo(() => {
    if (!formSchema) {
      return [];
    }
    return getFilteredArticulations(Array.from(articulations.values()), sourceInstitution, formSchema, searchTerm);
  }, [articulations, searchTerm, formSchema, sourceInstitution]);

  const filteredPrecedents = useMemo(() => {
    if (!formSchema) {
      return [];
    }
    return getFilteredPrecedents(Array.from(precedents.values()), sourceInstitution, formSchema, searchTerm);
  }, [formSchema, precedents, sourceInstitution, searchTerm]);

  if (!translation) {
    return <Spin />;
  }

  return (
    <Container className='search-results' id='search-results'>
      {/* @ts-expect-error: React 18 upgrade broke this package =( */}
      <StickyContainer>
        <Tabs
          className='tabs-container'
          centered
          defaultActiveKey={searchCategory}
          activeKey={searchCategory}
          onChange={(activeKey) => {
            if (activeKey === TabKey.ARTICULATION || activeKey === TabKey.PRECEDENT) {
              setSearchCategory(activeKey);
            }
          }}
          moreIcon={<EllipsisOutlined />}
          renderTabBar={(props, DefaultTabBar) => (
            // @ts-expect-error: React 18 upgrade broke this package =(
            <Sticky>
              {({ style, distanceFromTop }) => {
                const headerHeight = document.getElementById('header')?.offsetHeight ?? 0;
                const position = distanceFromTop <= (isMobile ? headerHeight : 0) ? 'fixed' : 'static';
                const top = isMobile ? headerHeight : distanceFromTop <= 0 ? headerHeight : 0;
                const width = isMobile ? '100vw' : style.width;
                return (
                  <DefaultTabBar
                    className='tab-bar'
                    {...props}
                    style={{
                      ...style,
                      display: 'flex',
                      justifyContent: 'center',
                      margin: 0,
                      zIndex: 1,
                      backgroundColor: 'white',
                      top,
                      position,
                      width,
                    }}
                  />
                );
              }}
            </Sticky>
          )}
        >
          <Tabs.TabPane
            tab={
              <TabPaneTab
                icon={translation.common.icons.articulationsTab}
                title={translation.home.searchResultTab.viewArticulationsTab.title}
                numberOfResults={filteredArticulations.length}
              />
            }
            key={TabKey.ARTICULATION}
          >
            <FlexContainer className='tab-pane-content'>
              {filteredArticulations.length > 0 && (
                <TabContentIntro className='tab-pane-intro'>
                  <Description className='tab-pane-description'>
                    {translation.home.searchResultTab.viewArticulationsTab.description}
                  </Description>
                  <Tooltip
                    className='tab-pane-tooltip'
                    title={
                      <TooltipText>
                        {translation.home.searchResultTab.viewArticulationsTab.tooltip.map((tooltip) => (
                          <div key={tooltip}>{tooltip}</div>
                        ))}
                      </TooltipText>
                    }
                    placement='right'
                  >
                    <TooltipIcon />
                  </Tooltip>
                </TabContentIntro>
              )}
              {searchCategory === TabKey.ARTICULATION && (
                <Fragment>
                  {filteredArticulations.length === 0 ? (
                    <ResultNotFound />
                  ) : (
                    <>
                      <Helmet>
                        <title>{translation.titles.home}</title>
                      </Helmet>
                      <ArticulationCreditList articulations={filteredArticulations} />
                    </>
                  )}
                </Fragment>
              )}
            </FlexContainer>
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={
              <TabPaneTab
                icon={translation.common.icons.precedentsTab}
                title={translation.home.searchResultTab.viewPrecedentsTab.title}
                numberOfResults={filteredPrecedents.length}
              />
            }
            key={TabKey.PRECEDENT}
          >
            <FlexContainer className='tab-pane-content'>
              {filteredPrecedents.length > 0 && (
                <TabContentIntro className='tab-pane-intro'>
                  <Description className='tab-pane-description'>
                    {translation.home.searchResultTab.viewPrecedentsTab.description}
                  </Description>
                  <Tooltip
                    className='tab-pane-tooltip'
                    title={
                      <TooltipText>
                        {translation.home.searchResultTab.viewPrecedentsTab.tooltip.map((tooltip) => (
                          <div key={tooltip}>{tooltip}</div>
                        ))}
                      </TooltipText>
                    }
                    placement='right'
                  >
                    <TooltipIcon />
                  </Tooltip>
                </TabContentIntro>
              )}
              {searchCategory === TabKey.PRECEDENT && (
                <Fragment>
                  {filteredPrecedents.length === 0 ? (
                    // <div className={`${styles.resultNotFoundContainer} result-not-found-container`}>
                    <ResultNotFound />
                  ) : (
                    // </div>
                    <>
                      <Helmet>
                        <title>{`Credit Search Results - ${translation.appName}`}</title>
                      </Helmet>
                      <PrecedentCreditList precedents={filteredPrecedents} />
                    </>
                  )}
                </Fragment>
              )}
            </FlexContainer>
          </Tabs.TabPane>
        </Tabs>
      </StickyContainer>
    </Container>
  );
}
